import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Modal, Select, Input, Row, Col, message, notification, InputRef } from 'antd';
import { trpc } from '../../../trpc';
import { User } from 'server/src/types/office-service-type';
import { jwtDecode } from 'jwt-decode';
import { allUserStatusOptions, userStatusCustomOption } from '../utils/userStatus';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (statusInput: string) => void
}

const UserStatusModal: React.FC<Props> = ({ open, onClose, onSave }) => {
  const [statusInput, setStatusInput] = useState<string>('');
  const [isCustomStatus, setIsCustomStatus] = useState<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const currentUserRoot = useMemo<User>(() => {
    return jwtDecode(new URLSearchParams(window.location.search).get('accessToken') ?? '');
  }, []); // Empty dependency array means it only runs once

  const { data: userStatus, isLoading: isLoadingStatus } = trpc.User.getStatusByUserId.useQuery({ userId: currentUserRoot.id });
  
  useEffect(() => {
    if (!isLoadingStatus && userStatus?.status) {
      setStatusInput(userStatus.status);
      setIsCustomStatus(!allUserStatusOptions.map(option => option.value).includes(userStatus.status));
    }
  }, [userStatus, isLoadingStatus]);

  useEffect(() => {
    if (textAreaRef.current) {
      const length = statusInput.length;
      textAreaRef.current.setSelectionRange(length, length);
      textAreaRef.current.focus();
    }
  }, [statusInput]);

  return (
    <div
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      onDoubleClick={(e: React.MouseEvent) => e.stopPropagation()}
      onWheel={(e: React.WheelEvent) => e.stopPropagation()}
      onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
    >
      <Modal
        title="ステータス設定"
        open={open}
        onOk={() => {
          if (!statusInput || statusInput.length < 1) {
            message.error('ステータスを入力する必要があります。');
            return;
          }
          onSave(statusInput);
          onClose();
        }}
        onCancel={onClose}
        destroyOnClose
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Select
              value={isCustomStatus ? userStatusCustomOption.value : statusInput}
              onChange={(value) => {
                if (value === userStatusCustomOption.value) {
                  setStatusInput('');
                  setIsCustomStatus(true);
                } else {
                  setIsCustomStatus(false);
                  setStatusInput(value);
                }
              }}
              style={{ width: '100%' }}
              placeholder="ステータスを選択してください"
            >
              {allUserStatusOptions.map((option) => (
                <Select.Option key={option.value} value={option.value} style={{ color: option.color }}>
                  <span>{option.label}</span>
                </Select.Option>
              ))}
            </Select>
          </Col>

          {isCustomStatus && (
            <Col span={24}>
              <Input.TextArea
                ref={(instance) => {
                  if (instance) {
                    textAreaRef.current = instance.resizableTextArea?.textArea || null;
                  }
                }} 
                value={statusInput}
                onChange={(e) => setStatusInput(e.target.value)}
                placeholder="ステータスを入力してください..."
                rows={4}
                autoFocus
                onDoubleClick={(e) => e.stopPropagation()}
                style={{ resize: 'none' }}
              />
            </Col>
          )}
        </Row>
      </Modal>
    </div>
  );
};

export default UserStatusModal;
